import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import GooglePlaceRating from '../GooglePlaceRating/GooglePlaceRating'
import Attachments from './common/Attachments'
import CornerControls from './common/CornerControls'
import { DetailCardAndPinLayout, DetailCardLayout } from './common/DetailCardAndPinLayout'
import ItemCardImage from './common/ItemCardImage'
import ItemCardImageCarousel from './common/ItemCardImageCarousel'
import PrintOnlyAddressAndNotes from './common/PrintOnlyAddressAndNotes'
import type { DetailItemCardVariant } from './common/types'

import clsx from 'clsx'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getImageUrl } from 'lib/image/imageUtils'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'
import { SHORT_TIME_HOUR_FORMAT_AM_PM_UPPERCASE } from 'constants/dateFormats'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import GroupWithSeparator from 'tripPlanner/components/CleanCard/common/GroupWithSeparator'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { usePlaceRating } from 'tripPlanner/hooks/Google/usePlaceRating'
import { useTripExplorePlaceDetails } from 'tripPlanner/hooks/Google/useTripExplorePlaceDetail'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type {
  AttractionItem,
  EventItem,
  RestaurantBarItem,
  ThingToDoItem,
} from 'tripPlanner/types/tripItem'
import { formatShortDateRange, maybePlaceId } from 'tripPlanner/utils'
import { getDisplayableCustomThingToDoName } from 'tripPlanner/utils/itemConversions/common'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import GooglePlacePriceLevel, { shouldShowPriceLevel } from '../GooglePlacePriceLevel'
import HotelInclusionLabel from '../ItemCard/HotelInclusionLabel'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import ConflictingTimesIcon from '../Itinerary/ConflictingTimesIcon'
import OutsideTripDatesIcon from '../Itinerary/OutsideTripDatesIcon'
import { checkIfTripItemHasOverlappingTimes, useItemOutsideOfTripDates } from '../Itinerary/utils'
import { startPlaceOfTripItem } from '../TripMaps/utils'
import EditTripItem from '../TripModal/TripItems/Edit'
import ContainerBreakpoint from '../util/ContainerBreakpoint'
import ItemTitle from './common/ItemTitle'
import CustomSuggestionCTAs from './PriceAndCTAs/CustomSuggestionCTAs'

const ReviewContainer = styled.div`
    min-height: ${rem(8)};
`

interface Props {
  item:
    | AttractionItem
    | RestaurantBarItem
    | EventItem
    | ThingToDoItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}
function DetailThingToDoCard({ item, variant }: Props) {
  const intl = useIntl()
  const [inFocus, , setInFocusOn, setInFocusOff] = useToggle(false)
  const [showComment, , setShowCommentOn, setShowCommentOff] = useToggle(!!item.notes)
  const canEdit = useCanEditTrip()

  const { data: trip } = useTrip({ tripId: item.tripId })

  const overlappingTimes = useMemo(() => {
    if (trip && item) {
      return checkIfTripItemHasOverlappingTimes(trip, item)
    }
    return {
      isOverlapping: false,
      previousItem: undefined,
      nextItem: undefined,
    }
  }, [trip, item])

  const place = startPlaceOfTripItem(item)
  const placeId = place ? maybePlaceId(place) : undefined

  const { data: placeRating, isLoading: placeRatingLoading } = usePlaceRating(placeId || '')

  const isFromExplore = (item.type === 'ATTRACTION' || item.type === 'RESTAURANT_BAR') &&
    item.sourceType === 'Explore'

  const { data: placeDetails } = useTripExplorePlaceDetails({
    placeIds: isFromExplore && placeId ? [placeId] : [],
  })

  const title = getDisplayableCustomThingToDoName(item)

  const dateDescription = formatShortDateRange(item)

  const imgId = item.type === 'ATTRACTION' ?
      (item.imageIds?.[0] ?? item.imageId) :
    undefined
  const imgUrl = place?.photoUrl

  const itemImages = useMemo(() => {
    if (item.type === 'ATTRACTION' && item.imageIds && item.imageIds.length > 0) {
      return item.imageIds.map(id => ({
        id,
        url: getImageUrl(id),
      }))
    }
    return []
  }, [item])

  const openModal = useOpenModal()

  const onClickAddTripItemDates = useCallback(() => {
    openModal(EditTripItem, { item })
  }, [item, openModal])

  const dateWarningIcon =
    variant !== 'drawer' ?
        <OutsideTripDatesIcon
          item={item}
          changeItemDates={onClickAddTripItemDates}
        /> :
      undefined

  const showDate = useItemOutsideOfTripDates(item) || variant !== 'itinerary'
  const startTime = item.startTime ? moment(item.startTime).format(SHORT_TIME_HOUR_FORMAT_AM_PM_UPPERCASE) : undefined
  const { itemNumber, itemColor } = useMemo(() => {
    if (place?.type === 'GEO' && place.point) {
      return getItemPinBadge(item, variant, trip)
    }
    return {
      itemNumber: undefined,
      itemColor: undefined,
    }
  }, [item, variant, trip, place])

  const showRating = placeId && !!placeRating?.rating && !placeRatingLoading
  const showPriceLevel = !!place?.priceLevel && shouldShowPriceLevel(place.priceLevel)
  const itemType = placeDetails?.[0]?.primaryTypeDisplayName

  const onRequestAddComment = useCallback(() => {
    setShowCommentOn()
    setInFocusOn()
  }, [setShowCommentOn, setInFocusOn])

  const onNotesChange = useCallback((notes: string | undefined) => {
    if (notes) {
      setShowCommentOn()
    } else {
      setShowCommentOff()
    }
  }, [setShowCommentOn, setShowCommentOff])

  const ImageComponent = useMemo(() => {
    if (itemImages.length > 1) {
      return (
        <>
          <ContainerBreakpoint min={640}>
            <ItemCardImageCarousel
              images={itemImages}
              className={clsx({ 'showComment': showComment })}
            />
          </ContainerBreakpoint>
          <ContainerBreakpoint max={640}>
            <ItemCardImage
              item={item}
              name={place?.name ?? ''}
              imgId={itemImages[0]?.id}
              imgSrc={itemImages[0]?.url}
              placeId={placeId}
              className={clsx({ 'showComment': showComment })}
            />
          </ContainerBreakpoint>
        </>
      )
    }

    return (
      <ItemCardImage
        item={item}
        name={place?.name ?? ''}
        placeId={placeId}
        imgId={imgId}
        imgSrc={imgUrl}
        className={clsx({ showComment })}
      />
    )
  }, [itemImages, item, place?.name, imgId, imgUrl, placeId, showComment])

  return (
    <DetailCardAndPinLayout data-testid="detail-card-thing-to-do">
      <DetailCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </DetailCardAndPinLayout.Pin>
      <DetailCardAndPinLayout.CardContainer>
        <DetailCardLayout.Image>
          <HoverOnlyRegenerateItemButton item={item} />
          {ImageComponent}
        </DetailCardLayout.Image>
        <DetailCardLayout.Content>
          <CornerControls item={item} onRequestAddComment={onRequestAddComment} />
          <VerticalSpacer gap={12}>
            <Group direction="vertical" gap={4}>
              <ItemTitle>{title}</ItemTitle>
              <GroupWithSeparator direction="horizontal" verticalAlign="center" gap={4} desktopGap={8} wrap="wrap">
                {showRating && <ReviewContainer>
                  <GooglePlaceRating placeId={placeId} size="S" />
                </ReviewContainer>}
                {itemType && (
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                  >
                    {itemType}
                  </BodyTextBlock>
                )}
                {showPriceLevel && <GooglePlacePriceLevel priceLevel={place.priceLevel} size="small" />}
                {dateDescription && showDate && (
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                    endIcon={dateWarningIcon}
                  >
                    {dateDescription}
                  </BodyTextBlock>
                )}
                {startTime && <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                  >
                    {startTime}
                  </BodyTextBlock>
                  {overlappingTimes.isOverlapping && (
                    <ConflictingTimesIcon
                      item={item}
                      changeItemDates={onClickAddTripItemDates}
                      previousItem={overlappingTimes.previousItem}
                      nextItem={overlappingTimes.nextItem}
                    />
                  )}
                </Group>}
                {item.isHotelInclusion && <HotelInclusionLabel />}
              </GroupWithSeparator>
            </Group>
            <Attachments item={item} />
            <ContainerBreakpoint min={640}>
              {item.insiderTip && (
                <Caption variant="medium">
                  <Caption variant="medium" colour="highlight-secondary" weight="bold" as="span">
                    {intl.formatMessage({
                      id: 'ren067xK_WCbxAB8ucc3',
                      defaultMessage: 'Insider Tip:',
                      description: 'Label prefix for insider tip text on a thing to do card',
                    })}&nbsp;
                  </Caption>
                  {item.insiderTip}
                </Caption>
              )}
            </ContainerBreakpoint>
            <PrintOnlyAddressAndNotes item={item} />
            {item.isSuggested && canEdit && (
              <CustomSuggestionCTAs item={item} />
            )}
          </VerticalSpacer>
        </DetailCardLayout.Content>
        {showComment && (
          <DetailCardLayout.Comment>
            <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              inFocus={inFocus}
              onFocus={setInFocusOn}
              onBlur={setInFocusOff}
              onNotesChange={onNotesChange}
            />
          </DetailCardLayout.Comment>
        )}
      </DetailCardAndPinLayout.CardContainer>
    </DetailCardAndPinLayout>
  )
}

export default DetailThingToDoCard
